// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Switch } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyActiveStore, TyOrderType } from "@/hooks/useStore/types";
import type { TyApiResponse } from "@/types";
import OutlinedField from "@/UI/components/OutlinedField";
import SettingsCard from "@/UI/components/SettingsCard";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyForm = {
    isEnabled: boolean;
    types: TyOrderType[];
};

// --- Constants ----------------------------------------------------------------------------------

/** Mirrors `ORDER_TYPES` in the backend — the order here is the order the menu shows them in. */
const TYPES: TyOrderType[] = ["delivery", "table", "pickup"];

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Helpers ------------------------------------------------------------------------------------

const toForm = (store: TyActiveStore | null): TyForm => ({
    isEnabled: store?.ordering?.isEnabled ?? false,
    // An empty stored list means the store never chose; the menu then offers everything.
    types: TYPES.filter((entry) => store?.ordering?.types?.includes(entry)),
});

// --- Logic --------------------------------------------------------------------------------------

/**
 * Whether the menu takes orders, and of what kind.
 *
 * Turning it on with nothing selected is refused by the API, so the save button stays out of reach
 * until at least one kind is picked — the store finds out here rather than from an error after
 * pressing save.
 */
export default function SettingsOrdering() {
    const t = useTranslation();
    const { canEdit } = usePermission();
    const storeId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);

    const editable = canEdit("settings");

    const { data: storeData } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store");
    const {
        data: saveData,
        loading: saving,
        error: saveError,
        invoke: save,
    } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store", { method: "PUT" }, false);

    const [store, setStoreData] = useState<TyActiveStore | null>(null);
    const [form, setForm] = useState<TyForm>(toForm(null));
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const fetched = storeData?.data?.store;
        if (fetched) setStoreData(fetched);
    }, [storeData]);

    useEffect(() => {
        if (store) setForm(toForm(store));
    }, [store]);

    useEffect(() => {
        if (!saveData?.success) return;
        const updated = saveData.data.store;
        setStoreData(updated);
        if (storeId) setStore(storeId, updated);
        setSaved(true);
        const timer = setTimeout(() => setSaved(false), 2600);
        return () => clearTimeout(timer);
    }, [saveData]);

    const initial = toForm(store);
    const dirty = Boolean(store) && JSON.stringify(form) !== JSON.stringify(initial);
    // Off is always a valid state; on has to be able to actually take an order.
    const isValid = !form.isEnabled || form.types.length > 0;

    const toggle = <T extends string>(list: T[], value: T, canonical: T[]) =>
        canonical.filter((entry) => (entry === value ? !list.includes(value) : list.includes(entry)));

    const handleSave = () => {
        if (!dirty || !isValid) return;
        save({
            method: "PUT",
            data: { ordering: { isEnabled: form.isEnabled, types: form.types } },
        });
    };

    return (
        <SettingsCard
            icon="/icons/side-bar/orders-icon-dark.svg"
            title={t.settingsOrdering.title}
            subtitle={t.settingsOrdering.subtitle}
            badge={editable ? undefined : t.settingsOrdering.readOnlyBadge}
            actions={
                editable
                    ? {
                          dirty,
                          saving,
                          saved,
                          error: saveError,
                          saveLabel: t.settings.saveButton,
                          savingLabel: t.settings.savingButton,
                          cancelLabel: t.settings.cancelButton,
                          savedLabel: t.settings.savedMessage,
                          onSave: handleSave,
                          onCancel: () => setForm(toForm(store)),
                      }
                    : undefined
            }
        >
            <div className="so-block">
                <div className="so-toggle">
                    <div className="so-toggle-text">
                        <span className="so-toggle-title">{t.settingsOrdering.enableLabel}</span>
                        <span className="so-hint">{t.settingsOrdering.enableHint}</span>
                    </div>
                    <Switch
                        checked={form.isEnabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, isEnabled: e.target.checked }))}
                        disabled={!editable || saving}
                        sx={switchSx}
                    />
                </div>

                {/* The rest is meaningless while ordering is off, so it stays out of the way. */}
                {form.isEnabled && (
                    <OutlinedField label={t.settingsOrdering.typesLabel}>
                        <div className="so-choices">
                            {TYPES.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className="so-choice"
                                    data-on={form.types.includes(type)}
                                    onClick={() => setForm((prev) => ({ ...prev, types: toggle(prev.types, type, TYPES) }))}
                                    disabled={!editable || saving}
                                >
                                    {type === "delivery"
                                        ? t.settingsOrdering.typeDelivery
                                        : type === "table"
                                          ? t.settingsOrdering.typeTable
                                          : t.settingsOrdering.typePickup}
                                </button>
                            ))}
                        </div>
                        <p className="so-hint">{t.settingsOrdering.typesHint}</p>
                    </OutlinedField>
                )}
            </div>
        </SettingsCard>
    );
}
